<template>
  <div>
    <div class="support-float">
      <div class="support-action">
        <el-tooltip content="AI 助手" placement="left" effect="dark">
          <el-button
            plain
            circle
            class="support-btn"
            aria-label="AI 助手"
            @click="handleAiClick"
          >
            <span class="support-ai-label">AI</span>
          </el-button>
        </el-tooltip>

        <el-tooltip content="建议反馈" placement="left" effect="dark">
          <el-button
            plain
            circle
            class="support-btn"
            aria-label="建议反馈"
            @click="openDrawer"
          >
            <el-icon class="support-icon"><QuestionFilled /></el-icon>
          </el-button>
        </el-tooltip>
      </div>
    </div>
  </div>

  <el-drawer
    v-model="drawerVisible"
    title="建议反馈"
    direction="rtl"
    size="min(440px, 92vw)"
    destroy-on-close
  >
    <el-form ref="feedbackFormRef" :model="form" :rules="formRules" label-position="top">
      <el-form-item label="反馈类型" prop="feedbackType" required>
        <el-radio-group v-model="form.feedbackType">
          <el-radio label="BUG">功能异常</el-radio>
          <el-radio label="SUGGESTION">产品建议</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="反馈内容" prop="content" required>
        <el-input
          v-model="form.content"
          type="textarea"
          :rows="14"
          maxlength="5000"
          show-word-limit
          placeholder="请按模板补充说明"
        />
      </el-form-item>
      <el-form-item label="联系方式（可选）">
        <el-input
          v-model="form.contact"
          maxlength="128"
          placeholder="请输入手机号或邮箱，便于回访"
        />
      </el-form-item>
      <el-form-item label="截图（可选，最多 5 张）">
        <el-upload
          v-model:file-list="fileList"
          list-type="picture-card"
          action="#"
          :auto-upload="false"
          accept="image/*"
          :limit="5"
          :on-change="handleImageChange"
          :on-exceed="handleImageExceed"
          :on-preview="handleImagePreview"
        >
          <el-icon><Plus /></el-icon>
          <template #tip>
            <div class="el-upload__tip">支持 jpg/png/gif 格式图片，大小不超过 5MB</div>
          </template>
        </el-upload>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="submitting" @click="handleSubmit"
          >提交反馈</el-button
        >
        <el-button @click="drawerVisible = false">取消</el-button>
      </el-form-item>
    </el-form>
  </el-drawer>

  <el-image-viewer
    v-if="previewVisible && previewUrlList.length"
    :url-list="previewUrlList"
    :initial-index="previewIndex"
    @close="previewVisible = false"
  />
</template>

<script>
import { ElMessage } from "element-plus";
import { Plus, QuestionFilled } from "@element-plus/icons-vue";
import { submitFeedback } from "@/api/feedback";
import { uploadFeedbackImage } from "@/api/upload";
import { compressImage, formatSizeMB } from "@/utils/imageCompress";

const MAX_FEEDBACK_IMAGE_MB = 5;
const MAX_SOURCE_IMAGE_MB = 20;

export default {
  name: "SupportFloat",
  components: {
    Plus,
    QuestionFilled,
  },
  data() {
    return {
      drawerVisible: false,
      previewVisible: false,
      previewIndex: 0,
      submitting: false,
      fileList: [],
      form: {
        feedbackType: "BUG",
        content: "",
        contact: "",
      },
      formRules: {
        feedbackType: [{ required: true, message: "请选择反馈类型", trigger: "change" }],
        content: [{ required: true, message: "请填写反馈内容", trigger: "blur" }],
      },
      defaultTemplate:
        "【问题页面/功能】\n" +
        "请填写出现问题或建议的页面、模块名称。\n" +
        "\n" +
        "【设备与浏览器信息】\n" +
        "例如：Windows + Chrome；Mac + Safari\n" +
        "\n" +
        "【复现步骤】\n" +
        " 1.\n" +
        " 2.\n" +
        " 3.\n" +
        "\n" +
        "【补充说明】\n" +
        "可补充出现频率、时间、账号场景等。",
    };
  },
  computed: {
    previewUrlList() {
      return this.fileList.map((f) => f.url).filter(Boolean);
    },
  },
  methods: {
    handleAiClick() {
      ElMessage.info("功能待实现");
    },
    openDrawer() {
      this.drawerVisible = true;
      if (!this.form.content.trim()) {
        this.form.content = this.defaultTemplate;
      }
    },
    async handleImageChange(file) {
      const raw = file?.raw;
      if (!raw) {
        this.removeFile(file.uid);
        return;
      }
      if (raw.size > MAX_SOURCE_IMAGE_MB * 1024 * 1024) {
        ElMessage.warning(`原图过大，请选择不超过 ${MAX_SOURCE_IMAGE_MB}MB 的图片`);
        this.removeFile(file.uid);
        return;
      }
      try {
        const compressed = await compressImage(raw, {
          maxSizeMB: MAX_FEEDBACK_IMAGE_MB,
          maxWidthOrHeight: 1920,
        });
        if (compressed.size > MAX_FEEDBACK_IMAGE_MB * 1024 * 1024) {
          ElMessage.warning(
            `压缩后仍为 ${formatSizeMB(
              compressed.size
            )}MB，超过 ${MAX_FEEDBACK_IMAGE_MB}MB，请换一张图`
          );
          this.removeFile(file.uid);
          return;
        }
        const res = await uploadFeedbackImage(compressed);
        if (res.data.code === 200) {
          const url = res.data.data;
          const source = this.fileList.find((f) => f.uid === file.uid);
          if (source) source.url = url;
          ElMessage.success("上传成功");
        } else {
          ElMessage.error(res.data.msg || "上传失败，请稍后重试");
          this.removeFile(file.uid);
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.msg || "上传失败，请稍后重试");
        this.removeFile(file.uid);
      }
    },
    handleImageExceed() {
      ElMessage.warning("最多上传 5 张图片");
    },
    handleImagePreview(file) {
      this.previewIndex = Math.max(this.previewUrlList.indexOf(file.url), 0);
      this.previewVisible = true;
    },
    removeFile(uid) {
      this.fileList = this.fileList.filter((f) => f.uid !== uid);
    },
    async handleSubmit() {
      const formRef = this.$refs.feedbackFormRef;
      this.submitting = true;
      try {
        const body = {
          feedbackType: this.form.feedbackType,
          content: (this.form.content || "").trim(),
          imageUrls: this.fileList.map((f) => f.url).filter(Boolean),
          contact: (this.form.contact || "").trim(),
        };
        const res = await submitFeedback(body);
        if (res.data.code === 200) {
          ElMessage.success("感谢反馈，我们已收到");
          this.drawerVisible = false;
          this.fileList = [];
          formRef.resetFields();
        } else {
          ElMessage.error(res.data?.msg || "提交失败");
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.msg || "提交失败");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.support-float {
  position: fixed;
  right: 20px;
  bottom: 24px;
  z-index: 1992;
  width: fit-content;
  pointer-events: none;
}

.support-action {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  margin-right: 20px;
  pointer-events: auto;
}

.support-btn {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  min-width: 44px;
  height: 44px;
  padding: 0;
}

.support-icon {
  font-size: 20px;
}

.support-ai-label {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
}

@media (max-width: 768px) {
  .support-float {
    display: none;
  }
}
</style>
